import { ReactNode, useEffect } from "react";
import { Location, useLocation } from "react-router-dom";

// The go host, a build-time setting. Unset (or empty) leaves every console
// list where it is.
export const GO_ROOT = (process.env.REACT_APP_GO_ROOT ?? "")
  .trim()
  .replace(/\/+$/, "");

// A console link that names its rows (`?insight=<key>` or `?id=<n>`) stays
// on the console list; go does not read those filters.
const IDENTIFIER_FILTERS = ["insight", "id"];

// The browser's location, behind a seam: jsdom makes `window.location` and
// its methods unforgeable, so a test swaps this getter instead.
export const browser = {
  location: (): typeof window.location => window.location,
};

/**
 * The go URL for this location: the same path and query string on the go
 * host, or `null` when no go host is set or the query string carries an
 * identifier filter.
 */
export function goRedirectTarget(
  location: Pick<Location, "pathname" | "search">,
): string | null {
  if (!GO_ROOT) return null;
  const params = new URLSearchParams(location.search);
  if (IDENTIFIER_FILTERS.some((key) => params.has(key))) return null;
  return `${GO_ROOT}${location.pathname}${location.search}`;
}

/**
 * Sends the request to the go host when `goRedirectTarget` gives a target,
 * else renders its children. Go is a different origin, so this is a browser
 * navigation rather than a router `Navigate`, and `replace` keeps the console
 * URL out of the history.
 */
export function GoRedirect({ children }: { children: ReactNode }) {
  const location = useLocation();
  const target = goRedirectTarget(location);

  useEffect(() => {
    if (target !== null) browser.location().replace(target);
  }, [target]);

  return target === null ? <>{children}</> : null;
}

export default GoRedirect;
